import { Component, HostListener, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { ADPResourceService } from "digital-core";
import { PasswordFormControls } from "../../../../shared/password/password-form/password-form.component";
import { AccountService } from "../../../../core/services/account.service";
import { AccountSignUpDto } from "../../../../core/services/sign-up/account-signup.dto";
import { PreInitSettingsService } from "../../../../core/services/settings/pre-init-setting.service";
import { SnackbarService } from "../../../../core/services/snackbar/snackbar.service";
import { Router } from "@angular/router";
import { ContractContactDetails, OtpChannel, SendOTPRequest } from "src/app/model/account/user-otp-request";
import { GoogleAnalyticsService } from "src/app/core/services/google-tag-manager.service";
import { GTMOnboardingEvents, OnboardingSteps } from "src/app/model/google-tag-manager/api-status";

enum CreateAccountFlow {
  lookupUserSetup,
  securitySetup,
  otpSetup,
  reviewContactSetup,
  reviewAddressSetup,
  contactSetup,
  passwordSetup,
  successStep
}

@Component({
  selector: 'app-create-account',
  templateUrl: './create-account.component.html',
  styleUrls: ['./create-account.component.scss']
})
export class CreateAccountComponent implements OnInit {
  // For template access
  public CreateAccountFlow = CreateAccountFlow;
  public heading: string;
  public lookupResponse: any;
  public otpRequest: SendOTPRequest;
  public currentScreenFlow: CreateAccountFlow = CreateAccountFlow.lookupUserSetup;
  public contactInfo = new ContractContactDetails();
  public decryptedCCDetails = new ContractContactDetails();
  public passwordForm = new FormGroup<PasswordFormControls>({});
  public signUpInProgress = false;
  public showContactInfo = false;
  public isReviewContactFinished = false;
  public isReviewContactDisabled = true;
  public skipReviewAddressStep = false;
  public isEmailOrPhoneUpdated = false;
  public isSPOEEnabled = false;
  public isSecondaryOTPNeeded = false;
  public contactChangeType: string;
  private otpSuccessfullyVerifiedToken?: string;
  private decryptedUserContact: string;

  public constructor(private router: Router,
    private resourceService: ADPResourceService,
    private accountService: AccountService,
    private preInitSettingsService: PreInitSettingsService,
    private snackbarService: SnackbarService,
    private googleAnalyticsService: GoogleAnalyticsService
  ) { }

  @HostListener('window:popstate', ['$event'])
  onPopState(event) {
    this.onClose();
  }
  public ngOnInit() {
    this.showContactInfo = this.resourceService.isPortalSettingTrue('Signup_Create_Account_ShowContactInfo');
    this.isSPOEEnabled  = this.resourceService.isPortalSettingTrue('Global_SPOE_Enabled');
    this.isReviewContactDisabled = this.resourceService.isPortalSettingTrue('Signup_Create_Account_DisableReviewContact');
  }

  public contactInfoSubmitted(contactInfo: ContractContactDetails) {
    this.contactInfo = contactInfo;
    this.decryptedCCDetails.addressLine1 = contactInfo.addressLine1;
    this.decryptedCCDetails.addressLine2 = contactInfo.addressLine2;
    this.decryptedCCDetails.city = contactInfo.city;
    this.decryptedCCDetails.state = contactInfo.state;
    this.decryptedCCDetails.zipCode = contactInfo.zipCode;
    this.decryptedCCDetails.phone = contactInfo.phone;
    this.googleAnalyticsService.emitOnboardingEvent(GTMOnboardingEvents.Complete, OnboardingSteps.ReviewAddress);
    this.goToPasswordStep();
  }

  private goToPasswordStep() {
    this.currentScreenFlow = CreateAccountFlow.passwordSetup;
    this.heading = this.resourceService.getViewLabel('Signup_Create_Account_Password_Heading_Text');
    this.googleAnalyticsService.emitOnboardingEvent(GTMOnboardingEvents.InProcess, OnboardingSteps.CreatePassword);
  }

  private goToContactStep() {
    this.currentScreenFlow = CreateAccountFlow.contactSetup;
    if (this.isSPOEEnabled) {
      this.heading = this.resourceService.getViewLabel('SPOE_Signup_Create_Account_ReviewAddress_Heading_Text');
    } else {
      this.heading = this.resourceService.getViewLabel('Signup_Create_Account_Contact_Heading_Text');
    }
  }

  private goToReviewAddress() {
    this.currentScreenFlow = CreateAccountFlow.reviewAddressSetup;
    if (this.isSPOEEnabled) {
      this.heading = this.resourceService.getViewLabel('SPOE_Signup_Create_Account_ReviewAddress_Heading_Text');
    } else {
      this.heading = this.resourceService.getViewLabel('Signup_Create_Account_ReviewAddress_Heading_Text');
    }
  }

  private goToReviewContact(doReset = false) {
    this.currentScreenFlow = CreateAccountFlow.reviewContactSetup;
    if (doReset) {
      this.contactInfo.primaryEmail = this.decryptedCCDetails.primaryEmail;
      this.contactInfo.phone = this.decryptedCCDetails.phone;
    }
    if (this.isSPOEEnabled) {
      if (this.contactInfo.primaryEmail === undefined || this.contactInfo.primaryEmail == null) {
        this.contactInfo.primaryEmail = this.lookupResponse.searchedEmail;
      }
      this.heading = this.resourceService.getViewLabel('SPOE_Signup_Create_Account_ReviewContact_Heading_Text');
    } else {
      this.heading = this.resourceService.getViewLabel('Signup_Create_Account_ReviewContact_Heading_Text');
    }
  }

  private goToVerifyOtpStep() {
    this.heading = this.resourceService.getViewLabel('Signup_Create_Account_VerifyOTP_Heading_Text');
    this.currentScreenFlow = CreateAccountFlow.otpSetup;
  }

  private goToSecurityVerification() {
    if (this.isReviewContactFinished) {
      this.heading = this.resourceService.getViewLabel('LookupUser_ReviewContact_Verify_Email_Address');
    } else {
      this.heading = this.resourceService.getViewLabel('LookupUser_VerificationOption_Text_Title');
      if (this.isSecondaryOTPNeeded) {
        this.heading = this.resourceService.getViewLabel('LookupUser_ReviewContact_SecondaryOTP_Verify');
      }
    }
    this.currentScreenFlow = CreateAccountFlow.securitySetup;
  }

  public lookupUserResponse(event) {
    this.lookupResponse = event;
    this.skipReviewAddressStep = !event.hasELP;
    this.showContactInfo = this.showContactInfo ? event.hasELP : false;
    this.goToSecurityVerification();
  }

  public createOTPResponse(event) {
    if (event == 'noConsentStep') {
      this.router.navigate(['/tech-support'], { queryParams: { headerKey: 'TechSupport_Signup_Create_Account_NoConsent' } });
    } else if (event?.userContact && event?.channel) {
      this.otpRequest = event;
      this.goToVerifyOtpStep();
    }
  }

  public verifyOTPResponse(response) {
    if (response.validatedOTPToken) {
      this.otpSuccessfullyVerifiedToken = response.validatedOTPToken;
      if (response.decryptedContractDetails) {
        // This 'if' condition is required to skip these assignments during otp-verification response for edit/update of email/phone
        response.decryptedContractDetails.email = response.searchedEmail;
        this.contactInfo = response.decryptedContractDetails;
        this.skipReviewAddressStep = !this.contactInfo.addressLine1 || !this.contactInfo.city || !this.contactInfo.state || !this.contactInfo.zipCode || !this.contactInfo.country;
        this.decryptedCCDetails = JSON.parse(JSON.stringify(response.decryptedContractDetails));
      }
      this.decryptedUserContact = response.userContact;
      this.isSecondaryOTPNeeded = !this.isSecondaryOTPNeeded ? this.lookupResponse.is2Step : false;
      if (this.isSecondaryOTPNeeded && response.decryptedContractDetails) {
        this.goToSecurityVerification();
      } else if (this.isReviewContactDisabled || this.isReviewContactFinished) {
        if (this.showContactInfo) {
          const areaCode = this.resourceService.getPortalSetting('Global_Phone_AreaCode').Value;
          if (this.contactInfo.phone?.startsWith(areaCode)) {
            this.contactInfo.phone = this.contactInfo.phone.substring(areaCode.length);
          }
          this.skipReviewAddressStep ? this.goToContactStep() : this.goToReviewAddress();
        } else {
          this.goToPasswordStep();
        }
      } else {
        this.goToReviewContact();
      }
    }
  }

  public reviewContactResponse(event) {
    this.isReviewContactFinished = true;
    this.isEmailOrPhoneUpdated = false;
    if (event && event.primaryEmail != this.decryptedCCDetails.primaryEmail) {
      this.isEmailOrPhoneUpdated = true;
      this.contactInfo.primaryEmail = event.primaryEmail;
      this.contactInfo.phone = event.phone;
      this.contactChangeType = 'email'; // This will be 'phone' when verify OTP is via Phone number
      this.goToSecurityVerification();
    } else if (this.showContactInfo) {
      this.skipReviewAddressStep ? this.goToContactStep() : this.goToReviewAddress();
    } else {
      this.goToPasswordStep();
    }
  }

  public reviewAddressResponse(isEdit) {
    if (isEdit) {
      this.goToContactStep();
    } else {
      this.goToPasswordStep();
    }
  }

  public previousScreen() {
    switch (this.currentScreenFlow) {
      case CreateAccountFlow.securitySetup:
        if (this.isReviewContactFinished) {
          this.isReviewContactFinished = false;
          this.goToReviewContact(true);
        } else if (this.isSecondaryOTPNeeded) {
          this.isSecondaryOTPNeeded = false;
          this.goToSecurityVerification();
        } else {
          this.currentScreenFlow = CreateAccountFlow.lookupUserSetup;
        }
        break;
      case CreateAccountFlow.otpSetup:
        if (this.isReviewContactFinished) {
          this.isReviewContactFinished = false;
          this.goToReviewContact(true);
        } else {
          this.goToSecurityVerification();
        }
        break;
      case CreateAccountFlow.reviewContactSetup:
        this.isReviewContactFinished = false;
        this.goToSecurityVerification();
        break;
      case CreateAccountFlow.reviewAddressSetup:
          this.isReviewContactFinished = false;
          if (!this.isReviewContactDisabled) {
            this.goToReviewContact(true);
          } else {
            this.goToSecurityVerification();
          }
        break;
      case CreateAccountFlow.contactSetup:
        this.skipReviewAddressStep ?
          (this.isEmailOrPhoneUpdated ? this.goToSecurityVerification() : this.goToReviewContact())
          : this.goToReviewAddress();
        break;
      case CreateAccountFlow.passwordSetup:
        // reset password form
        this.passwordForm.reset();
        if (this.showContactInfo) {
          this.skipReviewAddressStep ? this.goToContactStep() : this.goToReviewAddress();
        } else if (!this.isReviewContactDisabled) {
          this.isReviewContactFinished = false;
          this.goToReviewContact();
        } else {
          this.goToSecurityVerification();
        }
        break;
    }
  }

  public signUp(): void {
    this.googleAnalyticsService.emitOnboardingEvent(GTMOnboardingEvents.Complete, OnboardingSteps.CreatePassword);
    if (this.passwordForm.invalid || this.signUpInProgress) {
      return;
    }
    this.signUpInProgress = true;
    this.disableEnableInputs(true);

    if (!this.contactInfo.primaryEmail) {
      this.contactInfo.primaryEmail = this.decryptedUserContact;
    }
    const signupInfo: AccountSignUpDto = {
      primaryUserEmail: this.decryptedCCDetails.primaryEmail ?? this.decryptedUserContact,
      updatedUserEmail: this.contactInfo.primaryEmail ?? this.decryptedUserContact,
      updatedPhone: this.contactInfo.phone,
      userContact: this.decryptedUserContact,
      password: this.passwordForm.controls.password?.value,
      otpSuccessToken: this.otpSuccessfullyVerifiedToken,
      oktaPrefix: this.preInitSettingsService.getSiteSettings().oktaPrefix,
      groupId: this.preInitSettingsService.getSiteSettings().groupId,
      eprismPhone: this.lookupResponse.phoneNumbers?.length ? this.lookupResponse.phoneNumbers[0].split(':')[1] : null,
      isSPOEEnabled: this.isSPOEEnabled,
      firstName: this.contactInfo.firstName,
      lastName: this.contactInfo.lastName
    };

    this.accountService.signUpNewAccount(signupInfo).subscribe({
      next: () => {
        this.signUpInProgress = false;
        this.disableEnableInputs(false);
        this.snackbarService.showSimpleMessage(this.resourceService.getViewLabel('Signup_Create_Account_Successful_Snackbar_Message'));
        this.currentScreenFlow = CreateAccountFlow.successStep;
        this.googleAnalyticsService.emitOnboardingEvent(GTMOnboardingEvents.Complete, OnboardingSteps.CreateLogin);
      },
      error: (err: any) => {
        const emitError = {
          message: err?.error?.Message,
          type: err?.error?.ExceptionType,
          code: err?.error?.ErrorCode,
        }
        this.googleAnalyticsService.emitOnboardingEvent(GTMOnboardingEvents.Failed, OnboardingSteps.CreateLogin, emitError);
        if (err.error && err.error.Message) {
          if (err.error.Message.toLowerCase().indexOf('password (400, e0000001):') > -1) {
            err.error.Message = this.resourceService.getViewLabel('Global_Error_Code_Password_ece0000001');
          }
          // TODO: ERROR : Add the Logic in API to raise Error with right ErrorCode
          err.error.CustomTitle = err.error.Message;
        }
        this.signUpInProgress = false;
        this.disableEnableInputs(false);
        throw err;
      },
    });
  }

  /**
   * This method will enable / disable the input fields depending on whether
   * your pass a true or a false in. When you pass a true in, then it will
   * disable the inputs. When you pass a false in, then it will enable the
   * inputs.
   *
   * @param disabled When disabled is true, then the input fields will be
   * disabled. When disabled is false, then the input fields will be enabled.
   */
  private disableEnableInputs(disabled: boolean) {
    if (disabled === true) {
      this.passwordForm.disable();
    } else {
      this.passwordForm.enable();
    }
  }
  onClose() {
    this.router.navigate(['/login'], { queryParamsHandling: 'preserve' }).then(
      () => { window.location.reload(); });
  }
}
